/**
 * POS Cart Engine - Pure calculation engine for offline/local POS operations
 * Handles fast, zero-latency cart calculations, totals, discounts, taxes and promotions.
 */

/**
 * Parses numeric value safely
 */
export function parseNumber(val, fallback = 0) {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'number') return isNaN(val) ? fallback : val;

  let str = String(val).trim();
  if (!str) return fallback;

  // Se tiver vírgula e ponto (ex: "1.250,50"), remove pontos de milhar e troca vírgula por ponto
  if (str.includes(',') && str.includes('.')) {
    str = str.replace(/\./g, '').replace(',', '.');
  } else if (str.includes(',')) {
    // Se tiver apenas vírgula (ex: "4,64"), troca por ponto
    str = str.replace(',', '.');
  }
  // Se tiver apenas ponto (ex: "4.64"), é o formato padrão de float/JSON do JS

  const num = parseFloat(str);
  return isNaN(num) ? fallback : num;
}

/**
 * Calculates a single line item totals
 */
export function calculateLineItem(item) {
  const amount = parseNumber(item.amount, 1);
  const valueUn = parseNumber(item.valueUn || item.value, 0);
  const rawTotal = amount * valueUn;

  let discount = parseNumber(item.discount, 0);
  if (item.discountType === 'percent') {
    discount = (rawTotal * parseNumber(item.discountPercent, 0)) / 100;
  }

  const finalTotal = Math.max(0, rawTotal - discount);

  return {
    ...item,
    amount,
    valueUn,
    valueTotal: finalTotal,
    rawTotal,
    discount,
  };
}

/**
 * Evaluates and applies eligible local promotions to the cart items
 */
export function evaluatePromotions(items = [], activePromotions = [], selectedPromotionId = null, customer = null) {
  if (!activePromotions || activePromotions.length === 0) {
    return { appliedPromotion: null, discountAmount: 0, items };
  }

  let chosenPromo = null;

  if (selectedPromotionId && selectedPromotionId !== 1) {
    chosenPromo = activePromotions.find((p) => String(p.id) === String(selectedPromotionId));
  }

  if (!chosenPromo) {
    // Look for automatic public promotions
    chosenPromo = activePromotions.find((p) => {
      const isPublic = Boolean(p.isPublic);
      const isActive = p.isActive === undefined || Boolean(Number(p.isActive));
      return isPublic && isActive && !p.couponCode;
    });
  }

  if (!chosenPromo) {
    return { appliedPromotion: null, discountAmount: 0, items };
  }

  // Check minimum total value if specified
  const subtotal = items.reduce((sum, it) => sum + (parseNumber(it.valueUn) * parseNumber(it.amount)), 0);
  if (chosenPromo.minValue && subtotal < parseNumber(chosenPromo.minValue)) {
    return { appliedPromotion: null, discountAmount: 0, items };
  }

  // Parse categories or products in promotion
  let promoCategories = [];
  try {
    promoCategories = Array.isArray(chosenPromo.categories)
      ? chosenPromo.categories.map(Number)
      : (typeof chosenPromo.categories === 'string' ? JSON.parse(chosenPromo.categories).map(Number) : []);
  } catch (e) {
    promoCategories = [];
  }

  let promoProducts = [];
  try {
    promoProducts = Array.isArray(chosenPromo.products)
      ? chosenPromo.products.map(Number)
      : (typeof chosenPromo.products === 'string' ? JSON.parse(chosenPromo.products).map(Number) : []);
  } catch (e) {
    promoProducts = [];
  }

  const promoPercentage = parseNumber(chosenPromo.percentage, 0);
  const promoFixedDiscount = parseNumber(chosenPromo.discount, 0);
  let totalPromoDiscount = 0;

  const updatedItems = items.map((it) => {
    const itCat = it.category ? (typeof it.category === 'object' ? Number(it.category.id) : Number(it.category)) : null;
    const itId = Number(it.id);

    let isEligible = false;
    if (promoCategories.length > 0) {
      isEligible = itCat !== null && promoCategories.includes(itCat);
    } else if (promoProducts.length > 0) {
      isEligible = promoProducts.includes(itId);
    } else {
      // Global promotion applies to all items
      isEligible = true;
    }

    if (isEligible && promoPercentage > 0) {
      const raw = parseNumber(it.valueUn) * parseNumber(it.amount);
      const itemDisc = (raw * promoPercentage) / 100;
      totalPromoDiscount += itemDisc;
      return {
        ...it,
        promoDiscount: itemDisc,
        valueTotal: Math.max(0, raw - itemDisc),
      };
    }

    return it;
  });

  if (promoFixedDiscount > 0 && promoPercentage === 0) {
    totalPromoDiscount = Math.min(subtotal, promoFixedDiscount);
  }

  if (chosenPromo.maxDiscount && totalPromoDiscount > parseNumber(chosenPromo.maxDiscount)) {
    totalPromoDiscount = parseNumber(chosenPromo.maxDiscount);
  }

  return {
    appliedPromotion: chosenPromo,
    discountAmount: totalPromoDiscount,
    items: updatedItems,
  };
}

/**
 * Calculates entire cart totals
 */
export function calculateCartTotals({
  items = [],
  manualDiscount = 0,
  manualDiscountType = 'money', // 'money' | 'percent'
  appliedPromotion = null,
  promoDiscountAmount = 0,
  paymentMethod = null,
  plots = 1,
}) {
  const subtotal = items.reduce((sum, it) => sum + (parseNumber(it.valueUn) * parseNumber(it.amount)), 0);
  const totalItemsCount = items.reduce((sum, it) => sum + parseNumber(it.amount), 0);

  let calculatedManualDiscount = 0;
  if (manualDiscountType === 'percent') {
    calculatedManualDiscount = (subtotal * parseNumber(manualDiscount, 0)) / 100;
  } else {
    calculatedManualDiscount = parseNumber(manualDiscount, 0);
  }

  const totalDiscount = Math.min(subtotal, promoDiscountAmount + calculatedManualDiscount);
  const baseTotal = Math.max(0, subtotal - totalDiscount);

  // Interest/plots calculation
  let interestRate = 0;
  let interestValue = 0;

  if (paymentMethod && paymentMethod.interest && plots > 1) {
    let interestConfig = paymentMethod.interest;
    if (typeof interestConfig === 'string') {
      try {
        interestConfig = JSON.parse(interestConfig);
      } catch (e) {
        interestConfig = {};
      }
    }
    const rate = parseNumber(interestConfig[plots] || interestConfig[String(plots)], 0);
    if (rate > 0) {
      interestRate = rate;
      interestValue = (baseTotal * rate) / 100;
    }
  }

  const finalTotal = baseTotal + interestValue;

  return {
    itemsCount: totalItemsCount,
    subtotal,
    promoDiscount: promoDiscountAmount,
    manualDiscount: calculatedManualDiscount,
    totalDiscount,
    interestRate,
    interestValue,
    total: finalTotal,
    plots: plots || 1,
  };
}
